import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RightOutlined, SearchOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Spin, message } from 'antd';
import { useSelector } from "react-redux";
import { useLocation } from 'react-router-dom'


import BreadcrumbComponent from "../../common/BreadcrumbComponent";
import FilterComponent from "../../common/FilterComponent"
import RequestNudgeComponent from "../../nudge/RequestNudgeComponent"
import { fetchData, postData } from '../../../services/HomeService';
import ProductView from '.././ProductView';
import { getIcon } from '../../../utils';
import { SPIN_TEXT_MAP } from '../../../constants';

import styles from './style.module.css';

const _SEGMENT_LIST = ["economical", "value for money", "premium", "super premium"];


const generateNewQuoteId = () => 'ORD-' + Math.floor(Math.random() * 100000);


export default function ProductListing({ selectedParent, selectedChild, AvatarColor, feeds }) {
    const observerRef = useRef();
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const [searchQuery, setSearchQuery] = useState("");
    //TODO: Remove hard coded filters and implement in backend call
    const [selectedCategory, setSelectedCategory] = useState(new Set(_SEGMENT_LIST));

    const [productList, setProductList] = useState([]);
    const [canLoadMore, setCanLoadMore] = useState(true);
    const userData = useSelector((state) => state.user);
    const [inBasketCount, setInBasketCount] = useState(0);
    const [showCartShopModal, setShowCartShopModal] = useState(false);
    const [currPageInfo, setCurrPageInfo] = useState({ currTab: parseInt(queryParams.get('tab')) || 0, currPage: 1 });

    const handleChangeTab = (newTabIndex) => {
        if (currPageInfo.currTab === newTabIndex) return;

        // Reset all states in a single batch to avoid race conditions
        setCurrPageInfo({ currPage: 1, currTab: newTabIndex });
        setProductList([]);
        setCanLoadMore(true);
    }
    const getProductList = useCallback(async (scrollAction=false) => {
        if (currPageInfo.currPage <= 0 || feeds.length === 0 || !canLoadMore || loading) return;

        setLoading(true);
        const currTab = currPageInfo.currTab;

        const segmentFilter = selectedCategory.size ? `&segments=${[...selectedCategory].join(',')}` : '';
        const res = await fetchData(`${feeds[currTab].api.path}${segmentFilter}&limit=10&page=${currPageInfo.currPage}&sort=createdAt-desc`);

        if (!res) {
            setLoading(false);
            return;
        }

        if (res.data?.length < 10) {
            setCanLoadMore(false);
        }

        if (res.data?.length && scrollAction) {
            setProductList(prev => [...prev, ...res.data]);
            setCurrPageInfo(prev => ({ ...prev, currPage: prev.currPage + 1 }));
        } else{
            setProductList(res.data);
        }

        setInBasketCount(res.meta?.totalInBasket)
        setLoading(false);
    }, [currPageInfo, feeds, canLoadMore, loading, selectedCategory]);

    const handleReactProduct = async (product) => {
        const payload = {
            action: product.isLiked ? 'dislike' : 'like',
            actor: userData.id
        }

        const res = await postData(`/products/react/${product._id}`, payload);
        if (res !== null) {
            const tmpProductList = productList.map((val) => {
                if (val._id === product._id) 
                    return {
                        ...val,
                        isLiked: !product.isLiked,
                        totalLikes: (product?.totalLikes || 0) + (product.isLiked ? -1 : 1)
                    }
                return val;
            });

            setProductList(tmpProductList);
        }
    }

    const handleShopProduct = async (product) => {
        const payload = {
            action: product.inBasket ? 'remove' : 'add',
            actor: userData.id
        }

        const res = await postData(`/products/shop/${product._id}`, payload);
        if (res?.data) {
            const tmpProductList = productList.map((val) => {
                if (val._id === product._id) 
                    return {
                        ...val,
                        inBasket: !product.inBasket,
                    }
                return val;
            });

            setProductList(tmpProductList);
            setInBasketCount(inBasketCount + (!product.inBasket ? 1: -1))
            message.success(SPIN_TEXT_MAP[!product.inBasket ? 'PRODUCT_IN_BASKET' : 'PRODUCT_NOTIN_BASKET'], 2);
        } else {
            message.error(res?.message || "Some error happened!!", 2);
        }
    }

    const handleSearch = () => {
        // search using input
    }

    const closeCartShopModal = () => {
        setShowCartShopModal(false);
    }

    const handleRequestOrder = async (_productList, action) => {
        try {
            const quoteData = {
                quote: {
                    title: `${queryParams.get('title') || 'New Order'}`,
                    description: `Looking to place order for: \n ${_productList.map(pd => "\t* " + pd.name + " \n")} \n Delivery Address : \n \t`,
                    quoteId: generateNewQuoteId(),
                    status: 'draft',
                },
                skus: _productList.map(pd => pd.skuId),
                autoAssign: true
            }
            const res = await postData(`/products/cart/${action}`, quoteData);
            if (res?.data) {
                setProductList([]);
                setInBasketCount(0)
                message.success("Sucessfully placed order", 2);
            } else{
                message.error(res?.message || "Some error happened!!", 2);
            }
        } catch (error) {
            message.error(error?.message || "Some error happened!!", 2);
        } finally {
            setShowCartShopModal(false)
        }
    }

    const handleShoppingCart= async () => {
        if(currPageInfo.currTab === 1){
            setShowCartShopModal(true)
        }else{
            setCanLoadMore(true);
            setCurrPageInfo(prev => ({ ...prev, currTab: 1 }))
        }
    }

    const handleCategoryChange = (value) => {
        setCanLoadMore(true)
        if (value === "all") {
            setSelectedCategory(new Set());
            return;
        } else if(value === "reset"){
            setSelectedCategory(new Set(_SEGMENT_LIST));
            return;
        }
        const newSelectedCategory = new Set(selectedCategory);
        newSelectedCategory.delete(value);
        setSelectedCategory(newSelectedCategory);
    };

    const handleObserve = useCallback((entries) => {
        const [entry] = entries;
        // Only trigger if actually intersecting and not during fast scrolls
        if (entry.isIntersecting && !loading && canLoadMore && entry.intersectionRatio >= 0.1) {
            getProductList(true);
        }
    }, [getProductList, loading, canLoadMore]);

    const lastPostElementRef = useCallback(
        (node) => {
            if (loading) return;
            
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            
            if (node && canLoadMore) {
                observerRef.current = new IntersectionObserver(handleObserve, { 
                    root: null,
                    rootMargin: '0px',
                    threshold: 0.1
                });
                observerRef.current.observe(node);
            }
        },
        [loading, canLoadMore, handleObserve]
    );

    // Modify the useEffect to include currPageInfo.currTab as a dependency
    useEffect(() => {
        if (feeds.length > 0) {
            getProductList();
        }
    }, [feeds, currPageInfo.currTab]);

    useEffect(() => {
        getProductList();
    }, [selectedCategory]);

    return (
        <section className="Home_container">
            <section className="Home_firstContainer">
                <BreadcrumbComponent
                    selectedParent={selectedParent}
                    selectedChild={selectedChild}
                />
                <div
                    className="Home_heading"
                    style={{ justifyContent: 'space-between' }}
                >
                    <h1>{selectedChild}</h1>
                    <Button type="primary" onClick={handleShoppingCart}>
                        <ShoppingCartOutlined /> {`${currPageInfo.currTab === 1 ?'Request Order ': 'View Basket ' } ${inBasketCount ? `(${inBasketCount})` : ''}`}
                    </Button>
                </div>
            </section>

            <div className={styles.list}>
                <Row gutter={16}>
                    <Col lg={{ span: 18 }} md={{ span: 24 }}>
                        {
                            <FilterComponent
                                type={"product"}
                                categories={selectedCategory}
                                onCategoryChange={handleCategoryChange}
                            > </FilterComponent>
                        }
                    </Col>
                    <Col lg={{ span: 6 }} md={{ span: 24 }}>

                    </Col>
                </Row>
                <Row gutter={16}>
                    
                    <Col lg={{ span: 6 }} md={{ span: 24 }}>
                        {
                            feeds.map((val, i) => (
                                <div
                                    key={val.key}
                                    className={currPageInfo.currTab === i ? `${styles.tabs} ${styles.tabsActive}` : `${styles.tabs}`}
                                    onClick={() => handleChangeTab(i)}
                                >
                                    {getIcon(val.icon)}
                                    <span className={currPageInfo.currTab === i ? `${styles.tabText} ${styles.tabTextActive}` : `${styles.tabText}`}>
                                        {val.label}
                                    </span>
                                    <RightOutlined />
                                </div>
                            ))
                        }
                    </Col>
                    <Col lg={{ span: 12 }} md={{ span: 24 }}>
                        <Form onSubmitCapture={handleSearch}>
                            <Form.Item style={{ marginBottom: 10 }}>
                                <div className={styles.searchBox}>
                                    <Input
                                        placeholder='Search something'
                                        style={{ borderRadius: '28px' }}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <SearchOutlined
                                        className={styles.searchIcon}
                                        onClick={handleSearch}
                                    />
                                </div>
                            </Form.Item>
                        </Form>

                        {
                            productList.map((val, i) => (
                                <ProductView
                                    key={`productlist-${i}`}
                                    handleShopProduct={handleShopProduct}
                                    handleReactProduct={handleReactProduct}
                                    product={val}
                                    ref={i === productList.length - 1 ? lastPostElementRef : null}
                                />
                            ))
                        }
                        {loading && (
                            <div style={{ textAlign: 'center', marginTop: 20 }}>
                                <Spin />
                            </div>
                        )}
                        {!canLoadMore && (
                            <div style={{ textAlign: 'center', marginTop: 20 }}>
                                <p>No more products to display</p>
                            </div>
                        )}
                    </Col>
                    <Col lg={{ span: 6 }} md={{ span: 24 }}>

                    </Col>
                </Row>
            </div>
            <RequestNudgeComponent
                type={'requestOrder'}
                action={'order'}
                data={productList}
                isVisible={showCartShopModal}
                onClose={closeCartShopModal}
                handleNudgeSubmit={handleRequestOrder}
            />
        </section>
    );
}
